import React, { useState } from 'react';

import AlertSnackbar from '../../shared/components/AlertSnackbar';
import { ApiPortfolioState } from '../../dashboard/models/ApiPortfolioState';
import AuthService from '../../shared/services/AuthService';
import ConfirmationDialog from '../../shared/components/ConfirmationDialog';
import { LessonMarkingAction } from '../../content/components/LessonMarkingPanel';
import { LessonPlayerContextProvider } from '../../dashboard/models/LessonPlayerContext';
import { LessonPlayerMarkingContextProvider } from '../models/LessonPlayerMarkingContext';
import { LessonPlayerWithAnswers } from '../../shared/components/LessonPlayerWithAnswers';
import LessonPortfolioService from '../../assessments/services/LessonPortfolioService';
import Overlay from '../../shared/components/Overlay';
import SettingsService from '../../dashboard/services/SettingsService';
import { useEffect } from 'react';
import { useLessonVersion } from '../../content/hooks/useLessonVersion';
import useLessons from '../../content/hooks/useLessons';
import { useLocation } from 'react-router-dom';
import { usePortfolio } from '../hooks/usePortfolio';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface LessonPlayerMarkerViewProps {
  onClose: () => void;
}

export function LessonPlayerMarkerView(props: LessonPlayerMarkerViewProps) {
  const query = useQuery();
  const coursekey = +query.get('coursekey');
  const userkey = +query.get('userkey');
  const lessonkey = +query.get('modulekey');
  const lessonVersion = +query.get('moduleversion');
  const layer = +query.get('layer');

  const [portfolioState, setPortfolioState] = useState<ApiPortfolioState>(null);
  const [finalizing, setFinalizing] = useState<{ completed: boolean }>(null);
  const [canFinalize, setCanFinalize] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [userTotal, setUserTotal] = useState<number>(null);
  const [lessonTotal, setLessonTotal] = useState<number>(null);

  const { getLesson, loadingLesson, loadLessonError, lesson } = useLessons();

  const { lessonVersionAsset } = useLessonVersion(lessonkey, lessonVersion);

  const {
    getPortfolio,
    loadingPortfolio,
    portfolioError,
    portfolio,
    updateMark,
    updatingMark,
    updateMarkError,
    updatedMark,
    updatePortfolioState,
    updatingPortfolioState,
    updatePortfolioStateError,
    updatedPortfolio,
    completePortfolio,
    completingPortfolio,
    completePortfolioError,
    completedPortfolio
  } = usePortfolio();

  useEffect(() => {
    if (!lessonkey) {
      return;
    }
    getLesson(lessonkey);
    getPortfolio(userkey, lessonkey, layer);
  }, [lessonkey]);

  useEffect(() => {
    setPortfolioState(portfolio?.state);
  }, [portfolio]);

  useEffect(() => {
    if (updatedPortfolio?.state) {
      setPortfolioState(updatedPortfolio?.state);
    }
  }, [updatedPortfolio?.state]);

  useEffect(() => {
    //* The 'completed' date has been set.
    if (completedPortfolio?.completed) {
      const { userkey, modulekey, layer, state } = completedPortfolio;
      updatePortfolioState(
        userkey,
        modulekey,
        layer,
        ApiPortfolioState.AssessmentLock
      );
      setPortfolioState(ApiPortfolioState.AssessmentLock);
    }
  }, [completedPortfolio]);

  useEffect(() => {
    if (updatePortfolioStateError || completePortfolioError) {
      setErrorMessage('There was an error while updating.');
    }
  }, [updatePortfolioStateError, completePortfolioError]);

  function handleMarkLesson(action: LessonMarkingAction, completed: boolean) {
    if (action === 'finalize') {
      setFinalizing({ completed });
      return;
    }

    if (action === 'request_work') {
      updatePortfolioState(
        userkey,
        lessonkey,
        layer,
        ApiPortfolioState.AssessmentRetry
      );
      return;
    }
  }

  function handleFinalizeLesson(agreed: boolean) {
    const completed = finalizing.completed;
    setFinalizing(null);
    if (!agreed) {
      return;
    }

    if (completed) {
      completePortfolio(userkey, lessonkey, layer);
    } else {
      updatePortfolioState(
        userkey,
        lessonkey,
        layer,
        ApiPortfolioState.AssessmentLock
      );
    }
  }

  function handleUpdateMark(componentkey: number, mark: number) {
    updateMark(userkey, lessonkey, layer, componentkey, mark);
  }

  function handleCanFinalizeChange(
    value: boolean,
    userTotal: number,
    lessonTotal: number
  ) {
    setUserTotal(userTotal);
    setLessonTotal(lessonTotal);
    setCanFinalize(value);
  }

  function handleClose() {
    props.onClose();
  }

  const loggedInUserkey = AuthService.Userkey;
  const assignedToMe = portfolio?.markerUserkey === loggedInUserkey;
  const learner = portfolio
    ? `${portfolio.firstname} ${portfolio.lastname}`
    : '';
  const marker = LessonPortfolioService.getMarkerDisplayName(
    portfolio,
    loggedInUserkey
  );

  const completionType = SettingsService.convertCompletionType(
    lessonVersionAsset?.native?.completionFlags
  );

  const resultStatus = finalizing?.completed ? 'pass' : 'fail';

  return (
    <LessonPlayerContextProvider
      value={{
        preview: false,
        coursekey,
        lessonState: {
          answerParams: { userkey, lessonkey, layer },
          state: portfolioState
        }
      }}
    >
      <LessonPlayerMarkingContextProvider
        value={{
          state: portfolioState,
          canFinalize: canFinalize,
          assignedToMe,
          markerUserkey: portfolio?.markerUserkey
        }}
      >
        <LessonPlayerWithAnswers
          lessonkey={lessonkey}
          lessonVersion={lessonVersion}
          layer={layer}
          quizLocked={false}
          open
          displayMode="mark"
          lessonTitle={lesson?.title}
          lessonDescription={lesson?.description}
          lessonType={lesson?.type}
          displaySettings={lesson?.displaySettings}
          inProgress={loadingLesson}
          state={portfolioState}
          portfolio={portfolio}
          complete={!!portfolio?.completed}
          completionType={completionType}
          completionThreshold={lessonVersionAsset?.native?.threshold}
          learner={learner}
          marker={marker}
          onMarkLesson={handleMarkLesson}
          onUpdateMark={handleUpdateMark}
          onCanFinalizeChange={handleCanFinalizeChange}
          onClose={handleClose}
        />
        {(completingPortfolio || updatingPortfolioState) && (
          <Overlay spinner={false} message="Processing..." />
        )}
        {errorMessage && (
          <AlertSnackbar
            open={true}
            severity="error"
            message={errorMessage}
            onClose={() => {
              setErrorMessage('');
            }}
          />
        )}
        {finalizing && (
          <ConfirmationDialog
            open={true}
            title={`Are you sure you want to finalise the lesson?`}
            message={<>This operation isn't reversible. The learner currently has a score of {userTotal}/{lessonTotal} and will <b>{resultStatus}</b> the assessment.</>}
            onClose={handleFinalizeLesson}
          />
        )}
      </LessonPlayerMarkingContextProvider>
    </LessonPlayerContextProvider>
  );
}
